import { useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { $notificationStore, $transferRequestsCreateStore, $transferRequestsUpdateStore, $transferUpdateStatusStore } from '@lib/store';
import { useRouteHook } from '@shared/hooks';
import { RoutePaths } from '@shared/utils';
import type { AccountResponse } from '@shared/api/rest/accounts';
import type { RecipientResponse } from '@shared/api/rest/recipients';
import { TransferRequestStatusEnum, type TransferRequestResponse } from '@shared/api/rest/transferRequests';
import type { IFormDataOwnAccount, IFormDataBusinessBetween, IFormDataIndividualBetween } from '@pages/paymentBetweenAccounts/widgets';
import type { IFormDataBusinessOther, IFormDataIndividualOther } from '@pages/paymentOtherBank/widgets';
import type { IFormDataSettlementCard } from '@pages/paymentCardSettlement';
import { ConfirmModal, OTPModal, PaymentModal, TransferRequestConfirmModal } from '../modal';
import { Spinner } from '@shared/widgets';
import AlertIcon from '@assets/icons/alert.svg';
import ConfirmIcon from '@assets/icons/confirm.svg';

type TransferConfirmationDataType =
  | IFormDataOwnAccount
  | IFormDataIndividualBetween
  | IFormDataBusinessBetween
  | IFormDataIndividualOther
  | IFormDataBusinessOther
  | IFormDataSettlementCard;

interface TransferConfirmationBlockProps {
  showPaymentModal: boolean;
  onClosePaymentModal: () => void;
  account: AccountResponse;
  accountTo?: AccountResponse;
  recipient?: RecipientResponse | null;
}

export const TransferConfirmationBlock = (props: TransferConfirmationBlockProps) => {
  const { navigate } = useRouteHook();

  const transferRequestsCreateStore = useUnit($transferRequestsCreateStore.store);
  const transferUpdateStatusStore = useUnit($transferUpdateStatusStore.store);

  const [showApprovalModal, setShowApprovalModal] = useState<TransferRequestResponse | null>(null);
  const [showOtpModal, setShowOtpModal] = useState<TransferRequestResponse | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState<TransferRequestResponse | null>(null);

  const { t } = useTranslation('payments');
  const { watch, setValue } = useFormContext<TransferConfirmationDataType>();
  const files = watch('files');

  const sendHandler = async () => {
    if (!transferRequestsCreateStore.data?.id) return;
    const response = await $transferUpdateStatusStore.events.update({
      id: transferRequestsCreateStore.data?.id,
      status: TransferRequestStatusEnum.WaitingForApproval,
    });
    if (response.data) {
      props.onClosePaymentModal();
      setShowApprovalModal(response.data);
    }
  };

  const rejectHandler = () => {
    $transferRequestsCreateStore.events.clear();
    $transferRequestsUpdateStore.events.clear();
    $notificationStore.events.setWarning(t('notifications.rejected'));
  };

  return (
    <>
      {transferUpdateStatusStore.loading && <Spinner position />}
      <PaymentModal
        isOpen={props.showPaymentModal}
        onClose={props.onClosePaymentModal}
        account={props.account!}
        accountTo={props.accountTo}
        recipient={props.recipient}
        files={files}
        onUploadFile={(files) => setValue('files', files)}
        onDeleteFile={(files) => setValue('files', files)}
        onConfirm={sendHandler}
      />
      {showApprovalModal && (
        <TransferRequestConfirmModal
          isOpen={!!showApprovalModal}
          onClose={(withRedirect) => (setShowApprovalModal(null), withRedirect && navigate(RoutePaths.payments.onNavigate()))}
          withRedirect
          onOpenOtpModal={(data) => (setShowOtpModal(data), setShowApprovalModal(null))}
          onCloseOtpModal={() => setShowOtpModal(null)}
          transactionId={showApprovalModal.id}
          transferData={showApprovalModal}
          noDocumentUpload
          onReject={rejectHandler}
        />
      )}
      <OTPModal
        isOpen={!!showOtpModal}
        onClose={() => setShowOtpModal(null)}
        onCancel={() => (setShowApprovalModal(showOtpModal), setShowOtpModal(null))}
        data={showOtpModal!}
        onSubmit={(data) => setShowConfirmModal(data!)}
      />
      <ConfirmModal
        isOpen={!!showConfirmModal}
        icon={<ConfirmIcon />}
        title={(showConfirmModal?.totalAmount || 0) >= 50000 ? t('notifications.approved.title2') : t('notifications.approved.title')}
        description={
          <div className='mb-4 flex flex-col gap-2 text-center'>
            <p>{t('notifications.approved.subtitle')}</p>
            {(showConfirmModal?.totalAmount || 0) >= 50000 && <p>{t('notifications.approved.description')}</p>}
          </div>
        }
        buttonTitle='Transaction page'
        onClick={() => navigate(RoutePaths.accountTransaction.onNavigate(showConfirmModal!.paymentId, { back: RoutePaths.payments.onNavigate() }))}
      />
      <ConfirmModal
        isOpen={!!transferUpdateStatusStore.error}
        icon={<AlertIcon />}
        title={transferUpdateStatusStore.error ?? t('transferConfirmation.modals.confirm.error')}
        buttonTitle={t('buttons.return', { ns: 'common' })}
        onClick={() => $transferUpdateStatusStore.events.clear()}
      />
    </>
  );
};
